<template>
  <b-modal
    :id="modalId"
    ref="modal"
    size="lg"
    title="Invoice revision history"
    hide-footer
    modal-class="invoice-revision-history-shell"
    @shown="onShown"
    @hidden="onHidden"
  >
    <div v-if="activeInvoice" class="invoice-rev-history">
      <div class="invoice-rev-history__head">
        <div>
          <div class="invoice-rev-history__id">
            {{ activeInvoice.invoice_id || activeInvoice.invoice_number || 'Invoice' }}
          </div>
          <div class="invoice-rev-history__sub text-muted">
            Current invoice stays the latest version. Older rows are read-only snapshots.
          </div>
        </div>
        <div class="invoice-rev-history__badges">
          <b-badge v-if="statusLabel" variant="info">{{ statusLabel }}</b-badge>
          <span v-if="timesLabel" class="invoice-rev-history__times">{{ timesLabel }}</span>
        </div>
      </div>

      <div v-if="loading" class="text-muted small py-2">
        <b-spinner small class="mr-50" /> Loading stored versions…
      </div>

      <p v-else-if="!rows.length" class="invoice-rev-history__empty mb-0">
        No earlier versions stored. Invoices revised before version archive cannot show the old file or amounts.
        New revises keep the previous PDF and a snapshot on this invoice.
      </p>

      <div v-else class="invoice-rev-history__list">
        <div
          v-for="row in rows"
          :key="row.id"
          class="invoice-rev-history__row"
          :class="{ 'invoice-rev-history__row--open': selectedId === row.id }"
        >
          <div class="invoice-rev-history__row-main">
            <div class="min-w-0">
              <div class="invoice-rev-history__rev">Rev {{ row.revision_number }}</div>
              <div class="invoice-rev-history__meta text-muted">{{ rowMeta(row) }}</div>
            </div>
            <div class="invoice-rev-history__actions">
              <b-button
                size="sm"
                variant="outline-primary"
                class="py-0 px-1"
                :disabled="!canView(row)"
                @click="openRow(row)"
              >
                View
              </b-button>
              <b-button
                v-if="row.pdf_path"
                size="sm"
                variant="outline-secondary"
                class="py-0 px-1"
                @click="openPdf(row)"
              >
                PDF
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selected" class="invoice-rev-history__preview mt-2">
        <div class="invoice-rev-history__preview-title">
          Rev {{ selected.revision_number }} — read-only
        </div>
        <dl v-if="snapshotRows.length" class="invoice-rev-history__dl">
          <div v-for="field in snapshotRows" :key="field.key" class="invoice-rev-history__dl-row">
            <dt>{{ field.label }}</dt>
            <dd>{{ field.value }}</dd>
          </div>
        </dl>
        <p v-else class="text-muted small mb-2">
          No field snapshot on this revision — only a stored file is available if PDF is enabled.
        </p>
        <div v-if="previewUrl" class="invoice-rev-history__pdf">
          <iframe :src="previewUrl" title="Previous invoice PDF" />
        </div>
        <p v-else-if="selected.pdf_path && previewFailed" class="text-muted small mb-0">
          Stored PDF path could not be opened.
        </p>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BBadge, BButton, BSpinner } from 'bootstrap-vue'
import enhancedInvoiceRevisionService from '@/services/enhanced-invoice-revision-service'
import { loadInvoiceFileDataUrl } from '@/utils/invoice-file-loader'
import {
  collectRevisionSnapshots,
  formatRevisionRowMeta,
  formatRevisionTimesLabel,
  formatStatusWithRevisionCount,
  getInvoiceRevisionCount,
  snapshotFieldRows,
} from '@/utils/invoice-revision-count'

export default {
  name: 'InvoiceRevisionHistoryModal',
  components: { BModal, BBadge, BButton, BSpinner },
  props: {
    invoice: { type: Object, default: null },
    revisionHistory: { type: Array, default: null },
    modalId: { type: String, default: 'modal-invoice-revision-history' },
  },
  data() {
    return {
      activeInvoice: null,
      rows: [],
      loading: false,
      selectedId: null,
      previewUrl: null,
      previewFailed: false,
    }
  },
  computed: {
    revisionCount() {
      return getInvoiceRevisionCount(this.activeInvoice, this.rows)
    },
    statusLabel() {
      if (!this.activeInvoice) return ''
      return formatStatusWithRevisionCount(this.activeInvoice.status, this.revisionCount)
    },
    timesLabel() {
      return formatRevisionTimesLabel(this.revisionCount)
    },
    selected() {
      return this.rows.find((r) => r.id === this.selectedId) || null
    },
    snapshotRows() {
      return snapshotFieldRows(this.selected && this.selected.snapshot)
    },
  },
  watch: {
    invoice: {
      immediate: true,
      handler(val) {
        if (val) this.activeInvoice = val
      },
    },
  },
  methods: {
    rowMeta(row) {
      return formatRevisionRowMeta(row)
    },
    canView(row) {
      return !!(row && (row.snapshot || row.pdf_path))
    },
    async onShown() {
      this.activeInvoice = this.invoice
      this.selectedId = null
      this.previewUrl = null
      this.previewFailed = false
      await this.loadRows()
    },
    onHidden() {
      this.selectedId = null
      this.previewUrl = null
      this.rows = []
      this.$emit('close')
    },
    async loadRows() {
      this.loading = true
      try {
        let history = Array.isArray(this.revisionHistory) ? this.revisionHistory : null
        const id = this.activeInvoice && (this.activeInvoice.invoice_id || this.activeInvoice.invoice_number)
        if (!history && id) {
          const res = await enhancedInvoiceRevisionService.getRevisionHistory(id)
          const extracted = enhancedInvoiceRevisionService.extractRevisionRows(res)
          history = extracted.rows
        }
        this.rows = collectRevisionSnapshots(this.activeInvoice, history || [])
      } catch (_) {
        this.rows = collectRevisionSnapshots(this.activeInvoice, [])
      } finally {
        this.loading = false
      }
    },
    async openRow(row) {
      if (!this.canView(row)) return
      this.selectedId = row.id
      this.previewUrl = null
      this.previewFailed = false
      if (!row.pdf_path) return
      const url = await loadInvoiceFileDataUrl(row.pdf_path)
      if (url) this.previewUrl = url
      else this.previewFailed = true
    },
    async openPdf(row) {
      if (!row || !row.pdf_path) return
      const url = await loadInvoiceFileDataUrl(row.pdf_path)
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    },
  },
}
</script>

<style scoped>
.invoice-rev-history__head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}
.invoice-rev-history__id {
  font-weight: 700;
  color: #0099AC;
}
.invoice-rev-history__sub,
.invoice-rev-history__meta {
  font-size: 0.78rem;
}
.invoice-rev-history__badges {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.invoice-rev-history__times {
  font-size: 0.78rem;
  font-weight: 650;
  color: #475569;
}
.invoice-rev-history__empty {
  font-size: 0.85rem;
  color: #64748b;
}
.invoice-rev-history__row {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
  margin-bottom: 0.45rem;
}
.invoice-rev-history__row--open {
  border-color: #0099AC;
}
.invoice-rev-history__row-main {
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  align-items: flex-start;
}
.invoice-rev-history__rev {
  font-weight: 650;
  font-size: 0.85rem;
}
.invoice-rev-history__actions {
  display: flex;
  flex-shrink: 0;
  gap: 0.35rem;
}
.invoice-rev-history__preview {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  background: #f8fafc;
}
.invoice-rev-history__preview-title {
  font-weight: 650;
  font-size: 0.82rem;
  margin-bottom: 0.5rem;
}
.invoice-rev-history__dl {
  margin: 0 0 0.6rem;
}
.invoice-rev-history__dl-row {
  display: grid;
  grid-template-columns: 8.5rem 1fr;
  gap: 0.35rem;
  font-size: 0.8rem;
  margin-bottom: 0.2rem;
}
.invoice-rev-history__dl-row dt {
  color: #64748b;
  font-weight: 600;
}
.invoice-rev-history__dl-row dd {
  margin: 0;
}
.invoice-rev-history__pdf iframe {
  width: 100%;
  height: 380px;
  border: 0;
  background: #fff;
}
</style>
